import { Box, FormControl, FormHelperText, InputLabel, MenuItem, OutlinedInput, Select, SelectChangeEvent, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { FunctionComponent, MutableRefObject, useContext, useEffect, useMemo, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import TransactionService from '../../../../shared/services/transaction/transaction.service'
import CustomFieldService from '../../../../shared/services/custom-field/custom-field.service'
import SubsidiaryService from '../../../../shared/services/subsidiary/subsidiary.service'
import { ICustomField } from '../../../../shared/services/custom-field/custom-field.model'
import { ISubsidiary } from '../../../../shared/services/subsidiary/subsidiary.model'
import { ILocation } from '../../../../shared/services/location/location.model'
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications'
import { UserDetailsContext } from '../../../../context/user-details/user-details'
import { ScreenLabelsContext } from '../../../../context/screen-labels/screen-labels'
import { useIsReportPeriodLocked } from '../../../../hooks/use-is-report-period-locked'
import { getPreferredSubsidiary, getSelectableSubsidiaries } from '../../../../shared/utils/subsidiary-utils'
import { convertSqftToMTwo } from '../../../../shared/utils/conversion-utils'
import { formatTransaction } from '../../../../shared/utils/format-post-utils'
import { FileUploadHelper } from '../../miscellaneous-components/file-upload-helper'
import { DialogSaveButtons } from '../../miscellaneous-components/dialog-save-buttons'
import { EmissionsInputAdvanced } from './emissions-input/emissions-input-advanced-form'

// Maps the single-choice toggle to the consumptionTypes the backend expects.
type ConsumptionSelection = 'both' | 'electricity' | 'gas'
const CONSUMPTION_TYPES: Record<ConsumptionSelection, string[]> = {
  both: ['GAS', 'ELECTRICITY'],
  electricity: ['ELECTRICITY'],
  gas: ['GAS'],
}

// DEFRA conversion source: the only source the average-floor-space endpoint supports today.
const DEFRA_CONVERSION_SOURCE_ID = '5c7f57de-5209-4851-bd5e-eb1bb0e5273c'

/**
 * Estimates a location's utility emissions from its floor area. Extracted from the report-action
 * "utilities" card so it can be used anywhere (the location page, the add-data flow) without an
 * action. When a `groupId` is not supplied the backend books the estimate to Scope 3 Leased Assets,
 * exactly as the action did. Pass `submitRef`/`onSubmittingChange`/`hideActions` to drive it from a
 * DialogWrapper.
 */
export const FloorAreaEstimator: FunctionComponent<{
  location: ILocation,
  reportPeriodId: string,
  startDate: string,
  endDate: string,
  groupId?: string,
  reportActionId?: string,
  onSuccess: (result?: { emissionId?: string }) => void,
  onCancel?: () => void,
  hideActions?: boolean,
  submitRef?: MutableRefObject<(() => void) | undefined>,
  onSubmittingChange?: (isSubmitting: boolean) => void,
}> = ({
  location,
  reportPeriodId,
  startDate,
  endDate,
  groupId,
  reportActionId,
  onSuccess,
  onCancel,
  hideActions,
  submitRef,
  onSubmittingChange,
}) => {
    //Services
    const transactionService = new TransactionService()
    const customFieldService = new CustomFieldService()
    const subsidiaryService = SubsidiaryService.getInstance()
    //Context
    const userNotificationsContext = useContext(UserNotificationsContext)
    const userDetailsContext = useContext(UserDetailsContext)
    const userDetails = userDetailsContext.userDetails
    const screenLabelsContext = useContext(ScreenLabelsContext)
    //Hooks
    const isReportPeriodLocked = useIsReportPeriodLocked()
    //State
    const [subsidiaries, setSubsidiaries] = useState<ISubsidiary[]>()
    const [selectedSubsidiaryId, setSelectedSubsidiaryId] = useState<string>()
    const [customFields, setCustomFields] = useState<ICustomField[]>([])
    const [fileData, setFileData] = useState([])
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

    const transactionSchema = useMemo(() => Yup.object().shape({
      conversionSourceId: Yup.string().required('You must provide a conversion source'),
      unitsConsumed: Yup.number().min(0.01, 'The size must be greater than 0').required('You must provide an amount'),
      consumptionSelection: Yup.string().required('You must choose which utilities to estimate'),
      subsidiaryId: Yup.string().required(`You must provide a ${screenLabelsContext.getLabel('subsidiary', { plural: false })}`),
      projectId: Yup.string().nullable(),
      contactSupplierId: Yup.string().nullable(),
      countryId: Yup.string().required('You must provide a country'),
      vehicleId: Yup.string().nullable(),
      capitalGoodId: Yup.string().nullable(),
      excludeFromReporting: Yup.boolean(),
      excludeFromReportingReason: Yup.string().when('excludeFromReporting', {
        is: true,
        then: (schema) => schema.required('You must provide a reason for exclusion'),
        otherwise: (schema) => schema.nullable().notRequired(),
      }),
    }), [screenLabelsContext])

    const formik = useFormik<any>({
      initialValues: {
        conversionSourceId: DEFRA_CONVERSION_SOURCE_ID,
        unitsConsumed: location?.floorSpaceOrganisation || undefined,
        consumptionSelection: 'both' as ConsumptionSelection,
        subsidiaryId: '',
        countryId: '',
        projectId: null,
        contactSupplierId: null,
        vehicleId: null,
        capitalGoodId: null,
        excludeFromReporting: false,
        excludeFromReportingReason: '',
      },
      enableReinitialize: true,
      validationSchema: transactionSchema,
      onSubmit: (values) => submitHandler(values),
    })

    useEffect(() => {
      subsidiaryService.getAll()
        .then(response => setSubsidiaries(response))
        .catch(err => console.log(err))
      customFieldService.getAllActive(`&entryType=transaction`)
        .then(response => setCustomFields(response))
        .catch(err => console.log(err))
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
      if (userDetails && subsidiaries) {
        const preferred = getPreferredSubsidiary(subsidiaries, userDetails)
        if (preferred) {
          setSelectedSubsidiaryId(preferred.id)
        }
      }
    }, [userDetails, subsidiaries])

    useEffect(() => {
      if (subsidiaries && selectedSubsidiaryId) {
        const subsidiary = subsidiaries.find(s => s.id === selectedSubsidiaryId)
        if (subsidiary) {
          formik.setFieldValue('subsidiaryId', subsidiary.id)
          formik.setFieldValue('countryId', subsidiary.countryId)
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subsidiaries, selectedSubsidiaryId])

    // Let a parent DialogWrapper drive submission and disable its Save button while saving.
    useEffect(() => {
      if (submitRef) {
        submitRef.current = formik.submitForm
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submitRef, formik.submitForm])
    useEffect(() => {
      onSubmittingChange?.(isSubmitting)
    }, [isSubmitting, onSubmittingChange])

    const subsidiaryHandler = (event: SelectChangeEvent<string>) => {
      const subsidiary = subsidiaries?.find(s => s.id === event.target.value)
      if (subsidiary) {
        setSelectedSubsidiaryId(subsidiary.id)
        formik.setFieldValue('subsidiaryId', subsidiary.id)
        formik.setFieldValue('countryId', subsidiary.countryId)
      }
    }

    const consumptionHandler = (_event: React.MouseEvent<HTMLElement>, value: ConsumptionSelection | null) => {
      if (value !== null) {
        formik.setFieldValue('consumptionSelection', value)
      }
    }

    const submitHandler = (values: any) => {
      if (isReportPeriodLocked) {
        userNotificationsContext.openDialog({
          active: true,
          title: 'Action Not Allowed',
          message: `The selected report period is locked and cannot be modified`,
          buttonCopy: 'Ok',
          hideCancelButton: true
        })
        return
      }
      if (isSubmitting) {
        userNotificationsContext.openSnackbar({ open: true, message: 'Saving in progress, please wait' })
        return
      }
      setIsSubmitting(true)
      const payload = {
        ...values,
        reportPeriodId,
        startDate,
        endDate,
        groupId,
        locationId: location.id,
        // The user enters square feet; the backend stores that raw value and (for the legacy
        // transaction path) also wants it converted to m². The current emission engine reconverts.
        locationFloorSpace: values.unitsConsumed,
        unitsConsumed: convertSqftToMTwo(values.unitsConsumed),
        consumptionTypes: CONSUMPTION_TYPES[values.consumptionSelection as ConsumptionSelection],
      }
      const response = formatTransaction(payload, groupId ?? '', true, customFields)
      const form = new FormData()
      for (let i = 0; i < fileData.length; i++) {
        form.append('files', fileData[i])
      }
      form.append('transaction', JSON.stringify(response))
      if (reportActionId) {
        form.append('reportActionId', JSON.stringify(reportActionId))
      }
      transactionService.postTransactionAverageFloorSpace(form as any)
        .then((response) => {
          setIsSubmitting(false)
          onSuccess({ emissionId: (response as any)?.emissionId })
        })
        .catch(err => {
          console.log(err)
          setIsSubmitting(false)
          if (err?.response?.data?.code === 1) {
            userNotificationsContext.openSnackbar({ open: true, message: 'Report period is locked', backgroundColor: 'text.primary' })
          } else {
            const message = err?.response?.data?.message || 'There was an error, please try again'
            userNotificationsContext.openSnackbar({ open: true, message, backgroundColor: 'text.primary' })
          }
        })
    }

    if (!(customFields && subsidiaries && selectedSubsidiaryId)) {
      return null
    }

    // The occupied floor area is managed on the location (entered directly, or derived from building
    // floor area × share occupied). When it is set, show it read-only here so the estimate can't
    // diverge from the location record; when it isn't set yet, allow entering it inline (the backend
    // then saves it to the location).
    const storedOccupiedFloorArea = location?.floorSpaceOrganisation
    const hasStoredOccupiedFloorArea = storedOccupiedFloorArea != null && Number(storedOccupiedFloorArea) > 0

    return (
      <Box component='form' onSubmit={formik.handleSubmit}>
        <Typography variant='h6' sx={{ color: 'text.primary', pb: 2 }}>
          {hasStoredOccupiedFloorArea
            ? `Estimate utilities for ${location?.friendlyName ?? 'this location'} from its occupied floor area`
            : `Enter the occupied floor area for ${location?.friendlyName ?? 'this location'} to estimate its utilities`}
        </Typography>
        {(userDetails?.organisation?.enableSubsidiaries || subsidiaries.length > 1) && (
          <FormControl fullWidth variant='outlined' sx={{ mb: 2 }}>
            <InputLabel htmlFor='subsidiaryId'>{screenLabelsContext.getLabel('subsidiary', { capitalize: 'first' })}</InputLabel>
            <Select
              id='subsidiaryId'
              label={screenLabelsContext.getLabel('subsidiary', { capitalize: 'first' })}
              name='subsidiaryId'
              value={formik.values.subsidiaryId || ''}
              onChange={subsidiaryHandler}
              error={formik.touched.subsidiaryId && Boolean(formik.errors.subsidiaryId)}
              sx={{ color: 'text.primary' }}
            >
              {getSelectableSubsidiaries(subsidiaries).sort((a, b) => a.name.localeCompare(b.name)).map((subsidiary, index) => (
                <MenuItem key={index} value={subsidiary.id}>{subsidiary.name}</MenuItem>
              ))}
            </Select>
            {formik.touched.subsidiaryId && formik.errors.subsidiaryId && (
              <FormHelperText sx={{ color: '#bf3333', marginLeft: '16px !important' }}>
                <div>{formik.errors.subsidiaryId as string}</div>
              </FormHelperText>
            )}
          </FormControl>
        )}
        <Stack direction='row' spacing={2}>
          <FormControl fullWidth variant='outlined'>
            <InputLabel htmlFor='unitsConsumed'>Occupied floor area</InputLabel>
            <OutlinedInput
              inputProps={{ min: 0.01, step: 0.01 }}
              id='unitsConsumed'
              label='Occupied floor area'
              name='unitsConsumed'
              type='number'
              readOnly={hasStoredOccupiedFloorArea}
              value={formik.values.unitsConsumed}
              onChange={formik.handleChange}
              error={formik.touched.unitsConsumed && Boolean(formik.errors.unitsConsumed)}
              sx={{ color: 'text.primary' }}
            />
            {formik.touched.unitsConsumed && formik.errors.unitsConsumed && (
              <FormHelperText sx={{ color: '#bf3333', marginLeft: '16px !important' }}>
                <div>{formik.errors.unitsConsumed as string}</div>
              </FormHelperText>
            )}
            {hasStoredOccupiedFloorArea && (
              <FormHelperText sx={{ color: 'text.secondary', marginLeft: '16px !important' }}>
                Taken from this location. Change it under the location&apos;s Building Details.
              </FormHelperText>
            )}
          </FormControl>
          <FormControl variant='outlined' sx={{ flexBasis: '200px', flexShrink: 0 }}>
            <InputLabel htmlFor='measurementUnitId'>Sq ft</InputLabel>
            <Select id='measurementUnitId' name='measurementUnitId' label='units' value='' disabled sx={{ color: 'text.primary' }} />
          </FormControl>
        </Stack>
        <Box sx={{ mt: 2 }}>
          <Typography variant='body2' sx={{ color: 'text.secondary', pb: 1 }}>Which utilities should we estimate?</Typography>
          <ToggleButtonGroup value={formik.values.consumptionSelection} exclusive onChange={consumptionHandler}>
            <ToggleButton value='both' sx={{ pt: 1, pb: 0.75, px: 2.5 }}><Typography variant='body1' sx={{ color: 'text.primary', textTransform: 'capitalize' }}>Both</Typography></ToggleButton>
            <ToggleButton value='electricity' sx={{ pt: 1, pb: 0.75, px: 2.5 }}><Typography variant='body1' sx={{ color: 'text.primary', textTransform: 'capitalize' }}>Electricity</Typography></ToggleButton>
            <ToggleButton value='gas' sx={{ pt: 1, pb: 0.75, px: 2.5 }}><Typography variant='body1' sx={{ color: 'text.primary', textTransform: 'capitalize' }}>Gas</Typography></ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <EmissionsInputAdvanced
          customFields={customFields}
          formik={formik}
          hideRegulatedEmissionTradingScheme={true}
          hideEnergySupplierTariff={true}
          hideCustomBasedMarketMultiplier={true}
          hideCustomFields={false}
          hideExcludedFromReporting={false}
          hideLocation={true}
          hideRec={true}
        />
        {!hideActions && (
          <Stack direction='row' spacing={2} alignItems='center' justifyContent='flex-end' sx={{ mt: 3 }}>
            <FileUploadHelper fileData={fileData} setFileData={setFileData} disableButton={isSubmitting} />
            <DialogSaveButtons
              isForm={true}
              isSubmitting={isSubmitting}
              hideCancelButton={!onCancel}
              cancelHandler={onCancel}
              submitHandler={formik.submitForm}
            />
          </Stack>
        )}
        {hideActions && (
          <Stack direction='row' spacing={2} alignItems='center' justifyContent='flex-end' sx={{ mt: 3 }}>
            <FileUploadHelper fileData={fileData} setFileData={setFileData} disableButton={isSubmitting} />
          </Stack>
        )}
      </Box>
    )
  }
